/* eslint-disable @typescript-eslint/no-floating-promises */
/* eslint-disable @typescript-eslint/naming-convention */
/* eslint-disable react/jsx-no-useless-fragment */

'use client'

import { trackEvent } from '@acko-tech/building-blocks.ui.acko-analytics'
import { getR2d2Payload } from '@utils/r2d2Payload'
import { useEffect, useState } from 'react'
import { type AutoProduct } from 'src/enums/AutoProduct'
import { config, segmentModalType } from './config'
import { VEHICLE_MISMATCH_ICON } from './constants'
import IllogicalFlowOverlay from './illogicalFlowOverlay'
import getJourneyRedirect from './proposalFetchAndRedirect'
import { getModalType, getReverseAutoProduct, getSegmentPayload, redirectUrlWithQuery } from './util'

interface IllogicalFlowModalProps {
  errorCode: string
  illogicalFlowData: any
  onClose: Function
  product: AutoProduct
  identifiedVehicleType?: string
  regNo: string
  secondaryCta: Function
  showIllogicalFlowModal: boolean
  setShowIllogicalFlowModal: Function
  setProposalAPIResponse: Function
  combinedStyles?: any
}

const proposalAPIUrl = '/motororchestrator/api/v2/proposals'

const IllogicalFlowModal = ({
  errorCode,
  illogicalFlowData,
  onClose,
  product,
  identifiedVehicleType,
  regNo,
  secondaryCta,
  showIllogicalFlowModal,
  setShowIllogicalFlowModal,
  setProposalAPIResponse,
  combinedStyles,
}: IllogicalFlowModalProps) => {
  const isVehicleMismatchModal = errorCode === 'ILLOGICAL_FLOW_UNACCEPTABLE_ASSET'

  const [isFetching, setIsFetching] = useState(false)
  const modalType = getModalType(errorCode, illogicalFlowData, product)
  const { user_logged_in, last_digits, redirect_url } = illogicalFlowData

  const redirectToJourney = () => {
    window.location.href =
      !!redirect_url && user_logged_in ? redirectUrlWithQuery(redirect_url) : '/login?next=/myaccount'
  }

  const modalData = config[modalType]
  const isSingleCtaCase = modalData?.secondaryCtaText === null

  const illogicalFlowAdditionalSegmentEventProps = {
    mobile_verified: user_logged_in,
    context: segmentModalType[modalType],
    mobileLastDigits: last_digits,
    cc: illogicalFlowData?.cc,
    fuelType: illogicalFlowData?.fuel_type,
    is_renewal: illogicalFlowData?.is_renewal,
    vehicle_type: illogicalFlowData?.vehicle_type,
  }

  const getSegmentEventProps = (isVehicleMismatchModal, ekind) => {
    const segmentEventProps = {
      registration_number: regNo,
      product,
      ...getSegmentPayload(product),
      ...getR2d2Payload({
        ekind,
        edata: {
          registration_number: illogicalFlowData?.registration_number,
          isLoggedIn: user_logged_in,
          context: segmentModalType[modalType],
        },
        odata: { product },
      }),
    }

    if (isVehicleMismatchModal) return segmentEventProps
    return {
      ...segmentEventProps,
      ...illogicalFlowAdditionalSegmentEventProps,
    }
  }

  useEffect(() => {
    trackEvent(
      isVehicleMismatchModal ? `${identifiedVehicleType}_detected_modal_loaded` : 'policy_already_exists_modal_shown',
      {
        ...getSegmentEventProps(
          isVehicleMismatchModal,
          isVehicleMismatchModal ? `${identifiedVehicleType}_detected_modal_shown` : 'policy_already_exists_modal_shown'
        ),
      }
    )
  }, [showIllogicalFlowModal])

  return (
    <>
      {isVehicleMismatchModal ? (
        <IllogicalFlowOverlay
          regNo={regNo}
          icon={VEHICLE_MISMATCH_ICON}
          heading={`Uh-oh! Looks like this number belongs to a ${identifiedVehicleType}`}
          subHeading="How would you like to continue?"
          primaryCta={{
            ctaText: `Buy ${identifiedVehicleType} insurance`,
            onClick: async () => {
              await getJourneyRedirect(
                regNo,
                getReverseAutoProduct(product),
                proposalAPIUrl,
                setIsFetching,
                setShowIllogicalFlowModal,
                setProposalAPIResponse
              )
            },
            segmentEventName: `tap_btn_${identifiedVehicleType}_detected_modal_buy_${identifiedVehicleType}_insurance`,
            segmentEventProps: getSegmentEventProps(
              isVehicleMismatchModal,
              `${identifiedVehicleType}_redirection_cta_clicked`
            ),
          }}
          secondaryCta={{
            ctaText: 'Edit registration number',
            onClick: secondaryCta,
            segmentEventName: `tap_btn_${identifiedVehicleType}_detected_modal_edit_reg_no`,
            segmentEventProps: getSegmentEventProps(
              isVehicleMismatchModal,
              `edit_reg_on_${identifiedVehicleType}_modal_clicked`
            ),
          }}
          onClose={onClose}
          onCloseCtaSegmentEventName={`tap_btn_${identifiedVehicleType}_detected_modal_cross`}
          onCloseCtaSegmentEventProps={getSegmentEventProps(
            isVehicleMismatchModal,
            `tap_btn_${identifiedVehicleType}_detected_modal_cross`
          )}
          combinedStyles={combinedStyles}
        />
      ) : (
        <IllogicalFlowOverlay
          regNo={regNo}
          icon={{ src: modalData?.iconSrc, alt: modalData?.iconAlt }}
          heading={modalData?.heading}
          subHeading={
            user_logged_in || isSingleCtaCase ? modalData?.subHeading : modalData?.notLoggedSubHeading(last_digits)
          }
          primaryCta={{
            ctaText: user_logged_in || isSingleCtaCase ? modalData?.primaryCtaText : 'Log in',
            onClick: isSingleCtaCase ? onClose : redirectToJourney,
            segmentEventName: user_logged_in
              ? 'tap_btn_policy_already_exists_modal_view_policy'
              : 'tap_btn_policy_already_exists_modal_login',
            segmentEventProps: getSegmentEventProps(
              isVehicleMismatchModal,
              user_logged_in
                ? 'tap_btn_policy_already_exists_modal_view_policy'
                : 'tap_btn_policy_already_exists_modal_login'
            ),
            loading: isFetching,
          }}
          secondaryCta={
            modalData?.secondaryCtaText
              ? {
                  ctaText: modalData.secondaryCtaText,
                  onClick: secondaryCta,
                  segmentEventName: 'tap_btn_policy_already_exists_modal_edit_reg_no',
                  segmentEventProps: getSegmentEventProps(
                    isVehicleMismatchModal,
                    'tap_btn_policy_already_exists_modal_edit_reg_no'
                  ),
                }
              : undefined
          }
          onClose={onClose}
          onCloseCtaSegmentEventName="tap_btn_policy_already_exists_modal_cross"
          onCloseCtaSegmentEventProps={getSegmentEventProps(
            isVehicleMismatchModal,
            'tap_btn_policy_already_exists_modal_cross'
          )}
          combinedStyles={combinedStyles}
        />
      )}
    </>
  )
}

export default IllogicalFlowModal
